import { useState, useEffect, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { JsonRpcProvider, formatEther } from 'ethers';
import PaymentMethod from './PaymentMethod';
import GetEthHelper from './GetEthHelper';
import { getAppChainConfig } from '../utils/network';
import './WalletSection.css';

const METHOD_LABELS = {
  web3auth: 'Email / Social Login',
  walletconnect: 'WalletConnect',
  metamask: 'MetaMask',
};

const IconAddress = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
  </svg>
);
const IconNetwork = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const IconBalance = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const WalletSectionInner = forwardRef(function WalletSectionInner({
  paymentMethod,
  walletAddress,
  connecting,
  onPaymentMethodChange,
  onConnect,
  onDisconnect,
  creatorEmail,
  creatorEmailNotifyAll,
  creatorEmailReceiptsOnly,
  onCreatorEmailChange,
  onCreatorEmailNotifyAllChange,
  onCreatorEmailReceiptsOnlyChange,
}, ref) {
  const chainCfg = getAppChainConfig();
  const nativeSymbol = chainCfg.nativeSymbol || 'ETH';
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [ethMainnetBalance, setEthMainnetBalance] = useState(null);

  const fetchBalance = () => {
    if (!walletAddress || !chainCfg.rpcUrl) return;
    setBalanceLoading(true);
    const provider = new JsonRpcProvider(chainCfg.rpcUrl);
    provider.getBalance(walletAddress).then((bal) => {
      setBalance(formatEther(bal));
    }).catch(() => {
      setBalance(null);
    }).finally(() => {
      setBalanceLoading(false);
    });
  };

  useEffect(() => {
    if (!walletAddress || !chainCfg.rpcUrl) return;
    let cancelled = false;
    setBalanceLoading(true);
    const provider = new JsonRpcProvider(chainCfg.rpcUrl);
    provider.getBalance(walletAddress).then((bal) => {
      if (!cancelled) setBalance(formatEther(bal));
    }).catch(() => {
      if (!cancelled) setBalance(null);
    }).finally(() => {
      if (!cancelled) setBalanceLoading(false);
    });
    return () => { cancelled = true; };
  }, [walletAddress, chainCfg.rpcUrl]);

  // Cross-chain balance (Ethereum mainnet) when low on Base
  useEffect(() => {
    const minBet = parseFloat(process.env.REACT_APP_MIN_BET_NATIVE || '0.001');
    const isLow = balance != null && Number(balance) < minBet;
    if (!walletAddress || balance === null || !isLow) {
      setEthMainnetBalance(null);
      return;
    }
    let cancelled = false;
    const ethRpc = process.env.REACT_APP_ETH_MAINNET_RPC_URL || 'https://ethereum-rpc.publicnode.com';
    const provider = new JsonRpcProvider(ethRpc);
    provider.getBalance(walletAddress).then((bal) => {
      if (!cancelled) setEthMainnetBalance(formatEther(bal));
    }).catch(() => {
      if (!cancelled) setEthMainnetBalance(null);
    });
    return () => { cancelled = true; };
  }, [walletAddress, balance]);

  const minBet = parseFloat(process.env.REACT_APP_MIN_BET_NATIVE || '0.001');
  const hasLowBalance = !balanceLoading && balance != null && Number(balance) < minBet;
  const hasEnoughBalance = !balanceLoading && balance != null && Number(balance) >= minBet;

  return (
    <div className={`wallet-section ${connecting ? 'wallet-section-connecting' : ''}`} ref={ref}>
      {connecting && (
        <div className="wallet-section-loading">
          <div className="wallet-section-loading-spinner"></div>
          <p className="wallet-section-loading-text">Connecting wallet...</p>
          <p className="wallet-section-loading-hint">Please approve the connection in your wallet.</p>
        </div>
      )}
      <h4 className="wallet-section-title">Wallet</h4>

      <div className="wallet-section-body">
        {!walletAddress && (
          <div className="wallet-method-row">
            <PaymentMethod
              onSelect={onPaymentMethodChange}
              selectedMethod={paymentMethod}
              compact
            />
          </div>
        )}

        {!walletAddress ? (
          <div className="wallet-connect-row">
            <p className="wallet-connect-hint">Connect your wallet to create a challenge.</p>
            <button
              type="button"
              onClick={onConnect}
              disabled={connecting}
              className="wallet-connect-btn"
            >
              {connecting ? 'Connecting…' : 'Connect wallet'}
            </button>
          </div>
        ) : (
          <div className="wallet-status-row">
            <div className="wallet-status-header">
              <span className="wallet-status-badge">
                <span className="wallet-status-badge-icon"><IconCheck /></span>
                Connected
              </span>
              <span className="wallet-status-method">{METHOD_LABELS[paymentMethod] || paymentMethod}</span>
              {onDisconnect && (
                <button
                  type="button"
                  onClick={onDisconnect}
                  className="wallet-disconnect-btn"
                  aria-label="Disconnect wallet"
                >
                  Disconnect
                </button>
              )}
            </div>
            <div className="wallet-status-details">
              <div className="wallet-status-item">
                <span className="wallet-status-label"><span className="wallet-status-icon"><IconAddress /></span> Address</span>
                <span className="wallet-status-value">{walletAddress.slice(0, 6)}…{walletAddress.slice(-4)}</span>
              </div>
              <div className="wallet-status-item">
                <span className="wallet-status-label"><span className="wallet-status-icon"><IconNetwork /></span> Network</span>
                <span className="wallet-status-value">{chainCfg.chainName}</span>
              </div>
              <div className="wallet-status-item">
                <span className="wallet-status-label"><span className="wallet-status-icon"><IconBalance /></span> Balance</span>
                <span className="wallet-status-value wallet-status-balance-row">
                  {balanceLoading ? '…' : balance != null ? `${Number(balance).toFixed(4)} ${nativeSymbol}` : '—'}
                  <button
                    type="button"
                    onClick={fetchBalance}
                    disabled={balanceLoading}
                    className="wallet-refresh-btn"
                    aria-label="Refresh balance"
                    title="Refresh balance"
                  >
                    ↻
                  </button>
                </span>
              </div>
            </div>
            {hasEnoughBalance && (
              <div className="wallet-ready-status">
                <span className="wallet-ready-icon"><IconCheck /></span>
                <span className="wallet-ready-text">Ready to create challenge</span>
              </div>
            )}
            {hasLowBalance && (
              <div className="wallet-section-bridge">
                <GetEthHelper
                  walletAddress={walletAddress}
                  ethMainnetBalance={ethMainnetBalance}
                  autoOpenBridge={Number(balance) === 0}
                  onRefreshBalance={fetchBalance}
                  lowBalance={Number(balance) > 0}
                />
              </div>
            )}
            {walletAddress && onCreatorEmailChange && (
              <div className="wallet-section-email">
                <label className="wallet-section-email-label" htmlFor="creatorEmailInput">Email for updates (optional)</label>
                <input
                  id="creatorEmailInput"
                  type="email"
                  value={creatorEmail || ''}
                  onChange={e => onCreatorEmailChange(e.target.value)}
                  className="wallet-section-email-input"
                  placeholder="you@example.com"
                  aria-label="Optional email for challenge notifications"
                />
                <div className="wallet-section-email-options">
                  <label className="wallet-section-email-option">
                    <input
                      type="checkbox"
                      checked={creatorEmailNotifyAll || false}
                      onChange={e => onCreatorEmailNotifyAllChange?.(e.target.checked)}
                      aria-label="Notify me at each step"
                    />
                    <span>Notify me at each step (challenge created, opponent joined, result)</span>
                  </label>
                  <label className="wallet-section-email-option">
                    <input
                      type="checkbox"
                      checked={creatorEmailReceiptsOnly || false}
                      onChange={e => onCreatorEmailReceiptsOnlyChange?.(e.target.checked)}
                      aria-label="Only send receipt when finalized"
                    />
                    <span>Only send me a receipt when the challenge is finalized</span>
                  </label>
                </div>
                <small className="wallet-section-email-hint">Check at least one option above to receive emails. No spam.</small>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="wallet-section-footer">
        <Link to="/metamask-guide" className="wallet-guide-link">📖 Wallet setup help</Link>
      </div>
    </div>
  );
});

const WalletSection = WalletSectionInner;
export default WalletSection;
